import React from 'react';
import { TrendingUp, Target, Clock, Award, Download, Trophy, Flame } from 'lucide-react';
import { generateProgressPDF } from '../../utils/pdfGenerator';
import { getCategoryIds } from '../../data/categories';
import StreakTracker from './StreakTracker';
import DailyChallenge from '../gamification/DailyChallenge';
import XPBar from '../gamification/XPBar';
import LevelBadge from '../gamification/LevelBadge';
import { useLanguage } from '../../contexts/LanguageContext';

const ParentalDashboard = ({ 
  userProgress, 
  earnedBadges, 
  streakData = {}, 
  gamificationData = {}, 
  currentProfile,
  onShowActivityCalendar,
  onShowLeaderboard 
}) => {
  const { t } = useLanguage();
  // Get all category IDs from the categories data
  const categories = getCategoryIds();
  
  const getTotalStats = () => {
    const stats = {
      totalQuizzes: 0,
      totalScore: 0,
      totalPossible: 0,
      averageScore: 0,
      strongestCategory: '',
      weakestCategory: '',
      totalTimeSpent: 0
    };

    let bestPerformance = 0;
    let worstPerformance = 100;

    categories.forEach(category => {
      const categoryData = userProgress[category];
      if (categoryData) {
        stats.totalQuizzes += categoryData.totalQuizzes || 0;
        stats.totalScore += categoryData.totalScore || 0;
        stats.totalPossible += (categoryData.totalQuizzes || 0) * 10; // 10 questions per quiz
        
        // Use bestScore which is already a percentage
        const categoryBestScore = categoryData.bestScore || 0;

        if (categoryBestScore > bestPerformance) {
          bestPerformance = categoryBestScore;
          stats.strongestCategory = category;
        }
        if (categoryBestScore < worstPerformance && categoryBestScore > 0) {
          worstPerformance = categoryBestScore;
          stats.weakestCategory = category;
        }
      }
    });

    stats.averageScore = stats.totalPossible > 0 
      ? Math.round((stats.totalScore / stats.totalPossible) * 100)
      : 0;

    return stats;
  };

  const getCategoryStats = (category) => {
    const data = userProgress[category];
    if (!data) return null;

    // bestScore is already stored as a percentage
    // For average score, use bestScore if only 1 quiz completed
    // Otherwise calculate average from totalScore
    const averageScore = data.totalQuizzes === 1 
      ? Math.round(data.bestScore || 0)
      : data.totalQuizzes > 0 
        ? Math.round((data.totalScore / data.totalPossibleScore) * 100)
        : 0;

    return {
      totalQuizzes: data.totalQuizzes || 0,
      bestScore: Math.round(data.bestScore || 0), // Already a percentage
      averageScore: averageScore,
      lastPlayed: data.lastPlayed ? new Date(data.lastPlayed).toLocaleDateString() : 'Never'
    };
  };

  const getRecentActivity = () => {
    const activities = [];
    categories.forEach(category => {
      const data = userProgress[category];
      if (data && data.lastPlayed) {
        activities.push({
          category,
          date: new Date(data.lastPlayed),
          score: data.bestScore
        });
      }
    });
    
    return activities.sort((a, b) => b.date - a.date).slice(0, 5);
  };

  const handleDownloadProgress = () => {
    const progressData = {
      userProgress,
      earnedBadges,
      totalStats: getTotalStats(),
      date: new Date().toLocaleDateString()
    };
    generateProgressPDF(progressData);
  };

  const stats = getTotalStats();
  const recentActivity = getRecentActivity();

  return (
    <div className="dashboard animate-fadeIn">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <h2>{t('dashboard.title')}</h2>
        <button 
          className="action-button hover-lift"
          onClick={handleDownloadProgress}
        >
          <Download size={18} />
          {t('dashboard.downloadReport')}
        </button>
      </div>

      {/* Overall Stats */}
      <div className="progress-overview">
        <div className="progress-card animate-slideInLeft">
          <h4>{t('dashboard.totalQuizzes')}</h4>
          <div className="stat">
            <TrendingUp className="animate-pulse" size={24} style={{ color: 'var(--accent-color)' }} />
            <div className="stat-value">{stats.totalQuizzes}</div>
          </div>
        </div>

        <div className="progress-card animate-slideInLeft" style={{ animationDelay: '0.1s' }}>
          <h4>{t('dashboard.averageScore')}</h4>
          <div className="stat">
            <Target className="animate-pulse" size={24} style={{ color: 'var(--success-color)' }} />
            <div className="stat-value">{stats.averageScore}%</div>
          </div>
        </div>

        <div className="progress-card animate-slideInLeft" style={{ animationDelay: '0.2s' }}>
          <h4>{t('dashboard.badgesEarned')}</h4>
          <div className="stat">
            <Award className="animate-bounce" size={24} style={{ color: 'var(--warning-color)' }} />
            <div className="stat-value">{earnedBadges.length}</div>
          </div>
        </div>

        <div className="progress-card animate-slideInLeft" style={{ animationDelay: '0.3s' }}>
          <h4>{t('dashboard.bestCategory')}</h4>
          <div className="stat">
            <span style={{ fontSize: '1.5rem' }}>🏆</span>
            <div className="stat-value" style={{ textTransform: 'capitalize' }}>
              {stats.strongestCategory || 'None yet'}
            </div>
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="category-breakdown" style={{ marginBottom: '3rem' }}>
        <h3 style={{ 
          textAlign: 'center', 
          marginBottom: '2rem',
          color: 'var(--accent-color)',
          fontSize: '1.5rem'
        }}>
          {t('dashboard.categoryPerformance')}
        </h3>
        
        <div className="progress-overview">
          {categories.map((category, index) => {
            const categoryStats = getCategoryStats(category);
            if (!categoryStats) return null;

            return (
              <div 
                key={category} 
                className="progress-card animate-fadeIn"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <h4 style={{ textTransform: 'capitalize' }}>{category}</h4>
                <div className="stat">
                  <span>{t('dashboard.quizzesCompleted')}:</span>
                  <span className="stat-value">{categoryStats.totalQuizzes}</span>
                </div>
                <div className="stat">
                  <span>{t('dashboard.bestScore')}:</span>
                  <span className="stat-value">{categoryStats.bestScore}%</span>
                </div>
                <div className="stat">
                  <span>{t('dashboard.averageScoreLabel')}:</span>
                  <span className="stat-value">{categoryStats.averageScore}%</span>
                </div>
                <div className="stat">
                  <span>{t('dashboard.lastPlayed')}:</span>
                  <span className="stat-value" style={{ fontSize: '0.9rem' }}>
                    {categoryStats.lastPlayed}
                  </span>
                </div>
                
                {/* Progress Bar */}
                <div style={{ marginTop: '1rem' }}>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ 
                        width: `${categoryStats.averageScore}%`,
                        background: categoryStats.averageScore >= 80 
                          ? 'var(--success-color)'
                          : categoryStats.averageScore >= 60 
                          ? 'var(--warning-color)'
                          : 'var(--danger-color)'
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Activity */}
      {recentActivity.length > 0 && (
        <div className="recent-activity">
          <h3 style={{ 
            textAlign: 'center', 
            marginBottom: '2rem',
            color: 'var(--accent-color)',
            fontSize: '1.5rem'
          }}>
            {t('dashboard.recentActivity')}
          </h3>
          
          <div className="activity-list" style={{ 
            background: 'var(--secondary-bg)',
            borderRadius: '15px',
            padding: '2rem',
            boxShadow: 'var(--shadow)'
          }}>
            {recentActivity.map((activity, index) => (
              <div 
                key={index}
                className="activity-item animate-slideInRight"
                style={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '1rem',
                  borderBottom: index < recentActivity.length - 1 ? '1px solid var(--border-color)' : 'none',
                  animationDelay: `${index * 0.1}s`
                }}
              >
                <div>
                  <div style={{ fontWeight: '600', textTransform: 'capitalize' }}>
                    {activity.category} Quiz
                  </div>
                  <div style={{ color: 'var(--secondary-text)', fontSize: '0.9rem' }}>
                    {activity.date.toLocaleDateString()}
                  </div>
                </div>
                <div style={{ 
                  background: activity.score >= 80 
                    ? 'var(--success-color)' 
                    : activity.score >= 60 
                    ? 'var(--warning-color)' 
                    : 'var(--danger-color)',
                  color: 'white',
                  padding: '0.5rem 1rem',
                  borderRadius: '25px',
                  fontWeight: '600'
                }}>
                  {activity.score}%
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gamification Section */}
      {gamificationData.totalXP > 0 && (
        <div style={{ marginBottom: '3rem' }}>
          <h3 style={{ 
            textAlign: 'center', 
            marginBottom: '2rem',
            color: 'var(--accent-color)',
            fontSize: '1.5rem'
          }}>
            {t('gameZone.levelProgress')}
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
            <div>
              <LevelBadge totalXP={gamificationData.totalXP} size="large" showTitle={true} />
              <div style={{ marginTop: '1rem' }}>
                <XPBar totalXP={gamificationData.totalXP} showDetails={true} />
              </div>
            </div>
            
            <div>
              <button 
                className="action-button hover-lift"
                onClick={onShowLeaderboard}
                style={{ width: '100%', marginBottom: '1rem' }}
              >
                <Trophy size={18} />
                {t('gamification.viewLeaderboard')}
              </button>
              {currentProfile && (
                <DailyChallenge profileId={currentProfile.id} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Streak Section */}
      {streakData.currentStreak >= 0 && (
        <div style={{ marginBottom: '3rem' }}>
          <h3 style={{ 
            textAlign: 'center', 
            marginBottom: '2rem',
            color: 'var(--accent-color)',
            fontSize: '1.5rem'
          }}>
            <Flame size={24} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
            {t('gameZone.learningStreak')}
          </h3>
          <StreakTracker 
            streakData={streakData} 
            onShowCalendar={onShowActivityCalendar}
          />
        </div>
      )}

      {/* Badges Section */}
      {earnedBadges.length > 0 && (
        <div className="badges-section animate-fadeIn" style={{ marginBottom: '3rem' }}>
          <h3>{t('badges.earnedBadges')}</h3>
          <div className="badges-grid">
            {earnedBadges.map((badge, index) => (
              <div 
                key={badge.id} 
                className="badge-item animate-scaleIn hover-scale"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <span className="badge-icon">{badge.icon}</span>
                <div className="badge-name">{badge.name}</div>
                <div className="badge-desc">{badge.description}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      <div className="recommendations" style={{ 
        background: 'var(--secondary-bg)',
        borderRadius: '15px',
        padding: '2rem',
        marginTop: '2rem',
        boxShadow: 'var(--shadow)'
      }}>
        <h3 style={{ 
          textAlign: 'center', 
          marginBottom: '2rem',
          color: 'var(--accent-color)',
          fontSize: '1.5rem'
        }}>
          {t('badges.recommendations')}
        </h3>
        
        <div className="recommendations-list">
          {stats.weakestCategory && (
            <div className="recommendation-item animate-fadeIn" style={{ 
              padding: '1rem',
              background: 'var(--primary-bg)',
              borderRadius: '10px',
              marginBottom: '1rem'
            }}>
              <strong>{t('badges.focusArea').split(':')[0]}:</strong> {t('badges.focusArea').split(':')[1].replace('{category}', stats.weakestCategory)}
            </div>
          )}
          
          {stats.totalQuizzes < 10 && (
            <div className="recommendation-item animate-fadeIn" style={{ 
              padding: '1rem',
              background: 'var(--primary-bg)',
              borderRadius: '10px',
              marginBottom: '1rem',
              animationDelay: '0.1s'
            }}>
              <strong>{t('badges.practiceMore').split(':')[0]}:</strong> {t('badges.practiceMore').split(':')[1]}
            </div>
          )}
          
          {earnedBadges.length < 3 && (
            <div className="recommendation-item animate-fadeIn" style={{ 
              padding: '1rem',
              background: 'var(--primary-bg)',
              borderRadius: '10px',
              marginBottom: '1rem',
              animationDelay: '0.2s'
            }}>
              <strong>{t('badges.earnBadges').split(':')[0]}:</strong> {t('badges.earnBadges').split(':')[1]}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParentalDashboard;